// src/pages/Blog.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { fadeIn } from '../styles/animations';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { blogService, categoriesService } from '../services/api';

const BlogPageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const BlogHero = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.light};
  text-align: center;
`;

const HeroContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  color: ${props => props.theme.colors.light};
`;

const BlogContent = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.gray};
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid #eee;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const FilterTitle = styled.h3`
  margin: 0;
  margin-right: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.textMain};
  padding: 0.4rem 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  font-size: ${props => props.theme.fontSizes.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : '#f5f5f5'};
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.xs};
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.textMain};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : '#f5f5f5'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  h3 {
    margin-bottom: ${props => props.theme.spacing.md};
  }
  
  p {
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.textLight};
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textLight};
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.danger};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Blog = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [blogPosts, setBlogPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        per_page: 10
    });

    // Fonction pour formater les dates
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('fr-FR', options);
    };

    // Charger les catégories
    // categoriesService.getAll() returns a direct array
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await categoriesService.getAll();
                const list = Array.isArray(data) ? data : [];
                setCategories([
                    { id: 'all', name: 'Tous les articles' },
                    ...list
                ]);
            } catch (err) {
                console.error('Erreur lors du chargement des catégories:', err);
                setError('Erreur lors du chargement des catégories');
            }
        };

        fetchCategories();
    }, []);

    // Chargement des articles en fonction des filtres
    useEffect(() => {
        const fetchBlogPosts = async () => {
            try {
                setLoading(true);
                setError(null);

                // Paramètres pour l'API
                const params = {
                    page: currentPage,
                    limit: 6,  // Nombre d'articles par page
                    published: true
                };

                // Si une catégorie spécifique est sélectionnée
                if (selectedCategory !== 'all') {
                    const category = categories.find(c => String(c.id) === String(selectedCategory));
                    if (category) {
                        params.category_id = category.id;
                    }
                }

                let response;
                if (selectedCategory === 'all') {
                    response = await blogService.getAll(params);
                } else {
                    const category = categories.find(c => String(c.id) === String(selectedCategory));
                    if (category && category.slug) {
                        response = await blogService.getByCategory(category.slug);
                    } else {
                        response = await blogService.getAll(params);
                    }
                }

                // Mise à jour de l'état avec les données reçues
                if (response) {
                    if (response.posts) {
                        setBlogPosts(response.posts);
                        if (response.pagination) {
                            setPagination(response.pagination);
                        }
                    } else if (Array.isArray(response)) {
                        setBlogPosts(response);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Erreur lors du chargement des articles:', err);
                setError('Erreur lors du chargement des articles');
                setLoading(false);
            }
        };

        // Seulement charger les articles si les catégories sont chargées
        if (categories.length > 0) {
            fetchBlogPosts();
        }
    }, [selectedCategory, currentPage, categories]);

    // Calculer les pages pour la pagination
    const totalPages = pagination.total_pages || 1;

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        setCurrentPage(1); // Réinitialiser à la première page lors du changement de filtre
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <BlogPageWrapper>
            <Helmet>
                <title>Blog - Decko | Créateur de vos parquets de jardin</title>
                <meta name="description" content="Découvrez nos articles et conseils sur les terrasses en bois, l'aménagement extérieur et nos dernières réalisations." />
            </Helmet>

            <BlogHero>
                <HeroContainer>
                    <HeroTitle>Notre Blog</HeroTitle>
                    <HeroSubtitle>Conseils, inspirations et dernières réalisations</HeroSubtitle>
                </HeroContainer>
            </BlogHero>

            <BlogContent>
                <ContentContainer>
                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <FilterBar>
                        <FilterTitle>Filtrer par :</FilterTitle>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {categories.map(category => (
                                <FilterButton
                                    key={category.id}
                                    active={selectedCategory === String(category.id ?? '')}
                                    onClick={() => handleCategoryChange(String(category.id ?? ''))}
                                >
                                    {category.name}
                                </FilterButton>
                            ))}
                        </div>
                    </FilterBar>

                    {loading ? (
                        <LoadingIndicator>
                            <p>Chargement des articles...</p>
                        </LoadingIndicator>
                    ) : blogPosts.length > 0 ? (
                        <>
                            <BlogGrid>
                                {blogPosts.map(post => (
                                    <Card
                                        key={post.id}
                                        image={post.featured_image
                                            ? `https://decko.fr/uploads/${post.featured_image}`
                                            : 'https://via.placeholder.com/400x200?text=Decko+Blog'}
                                        imageHeight="200px"
                                        imageAlt={post.title}
                                        title={post.title}
                                        titleLink={`/blog/${post.slug}`}
                                        text={post.excerpt}
                                        textLimit={3}
                                        date={formatDate(post.created_at)}
                                        category={post.category_name || ''}
                                        footer={
                                            <Button
                                                variant="text"
                                                to={`/blog/${post.slug}`}
                                            >
                                                Lire l'article
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                    <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                                </svg>
                                            </Button>
                                        }
                                    />
                                ))}
                            </BlogGrid>

                            {totalPages > 1 && (
                                <Pagination>
                                    <PageButton
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        &lt;
                                    </PageButton>

                                    {[...Array(totalPages)].map((_, i) => (
                                        <PageButton
                                            key={i + 1}
                                            active={currentPage === i + 1}
                                            onClick={() => paginate(i + 1)}
                                        >
                                            {i + 1}
                                        </PageButton>
                                    ))}

                                    <PageButton
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        &gt;
                                    </PageButton>
                                </Pagination>
                            )}
                        </>
                    ) : (
                        <NoResults>
                            <h3>Aucun article trouvé</h3>
                            <p>Aucun article ne correspond à votre recherche pour la catégorie sélectionnée.</p>
                            <Button
                                variant="secondary"
                                onClick={() => handleCategoryChange('all')}
                            >
                                Voir tous les articles
                            </Button>
                        </NoResults>
                    )}
                </ContentContainer>
            </BlogContent>
        </BlogPageWrapper>
    );
};

export default Blog;